import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Trash2, Bell, BellOff, FileText, CalendarDays, Check, X,
  ChevronLeft, ChevronRight, CheckCircle2, Ban, Clock3, AlertTriangle,
  Users, Search, MessageCircle,
} from 'lucide-react';
import {
  calcularAvisarEm, descreverAviso, chaveDoDia, horaDoDia, rotuloDeDia, instanteNoFuso,
  contar, AVISO_HORAS_MAX, type ModoAviso,
  estadoDoCompromisso, pinturaDoEstado, seloDoCompromisso, faixaDaGrade, ANDAMENTOS,
  type StatusCompromisso,
} from '@fiodecobre/shared';
import {
  appApi, ErroApp,
  type CompromissoApp, type CorpoCompromisso, type ResumoOrcamentoApp,
  type ClienteFinalApp, type CategoriaAgendaApp,
} from './api';
import { estiloCampo } from './campos';
import { ComboBox } from '../../components/ComboBox';
import { ClientesApp, ModalNovoClienteFinal } from './ClientesApp';

// AppCel — AGENDA: os compromissos de trabalho do PRESTADOR.
//
// Escopo fechado por ele em 18/08/2026 ([DA-575] p.9): Data, Hora, Cliente, Orcamento
// vinculado e Observacoes. Construida em 19/08, com o "Avisar quando?" que ele
// acrescentou na hora de mandar construir.
//
// ⚠️ **O "Cliente" virou COMBO, escolhido do cadastro DELE** (onda de 29-30/08/2026,
// escopo fechado com o Ruan) — `app_clientes_finais`, tela propria em
// `ClientesApp.tsx`. Continua sendo *"cliente do usuario, nao os nossos"*: NAO ha
// busca nem autocompletar com ficha da LOJA (`clientes`), so com o cadastro que ele
// mesmo mantem. O campo `cliente_final` (texto livre) CONTINUA existindo e sendo
// gravado junto — `cliente_id` e' um ponteiro OPCIONAL que convive com ele, nao uma
// substituicao (ha compromissos antigos so com o texto, e isso segue funcionando).
//

// ⚠️ **A forma e' a GRADE DO DIA (07h–19h), nao calendario de mes nem lista corrida.**
// Pedido dele em 19/08/2026, horas depois de a lista subir: *"A tela inicial da
// agenda deve mostrar a agenda do dia, das 07 as 19 horas. Os agendamentos marcados
// devem ser exibidos"*, com cor por estado. O criterio de aceite continua sendo TEMPO
// ([DA-565]): a grade abre em HOJE e as horas vazias sao a resposta pra *"tenho
// espaco as 14h?"*, que a lista corrida nao respondia — ela so mostrava o que existe,
// nunca o que falta.
//
// ⚠️ **A faixa ESTICA** pra caber compromisso fora do horario comercial
// (`faixaDaGrade`, no shared): 07–19 e' onde ela ABRE, nao um filtro. Agenda que
// esconde compromisso e' pior que agenda nenhuma, porque ele confia nela.
//
// ⚠️ **As abas "Proximos/Anteriores" SAIRAM** com a grade (decisao dele, 19/08) — a
// navegacao virou dia a dia pelas setas. Nao repor: dois jeitos de ler a mesma coisa
// na mesma tela foi exatamente o que ele cortou.

const COBRE = '#b45309';
const CINZA = '#6b7280';
const BORDA = '#e7e5e4';
/** O destaque da casa. Verde e vermelho nao servem de sinal (daltonismo). */
const DESTAQUE = '#fde68a';

/** Os atalhos de antecedencia, em horas. Digitar "24" custa mais que tocar "1 dia". */
const ATALHOS_HORAS = [1, 2, 12, 24];

/**
 * A COR DE CADA CHIP da folha — pedido dele em 20/08/2026, vendo a folha pronta:
 * *"gostaria que todos os selos ja fossem exibidos na cor atribuida"*.
 *
 * ⚠️ **A cor aparece no chip APAGADO tambem, e e' o pedido inteiro.** Chip de um grupo
 * e' exclusivo: so um acende por vez. Pintar so o aceso trocaria o cobre por outra cor
 * e continuaria mostrando **uma cor de cada vez** — a cor seguiria dizendo *qual esta
 * ligado*, nunca *o que ele e'*. Foi o que o mockup de tres colunas mostrou, e a
 * escolha dele foi a coluna em que as cores convivem na tela.
 *
 * ⛔ **Nao reaproveitar a paleta da grade** (`pinturaDoEstado`: azul, vermelho, laranja,
 * ambar). Na folha de um compromisso JA SALVO a regua ANDAMENTO fica logo abaixo destes
 * chips, pintada com aquelas cores e com significado — azul e' *concluido*, vermelho e'
 * *vencido*. Um chip de aviso em azul faria o mesmo azul dizer duas coisas na mesma
 * tela. Ciano e violeta entraram por serem os hues que ali ainda nao significam nada.
 *
 * ⚠️ Sem par verde/vermelho em lugar nenhum ([[user_daltonismo]]) — e a palavra do chip
 * continua sendo o sinal principal, a cor entra POR CIMA dela
 * ([[feedback_acrescentar_redundancia_nao_trocar]]).
 */
interface CorChip {
  /** Borda sempre, e a letra do chip apagado. */
  forte: string;
  /** O preenchimento do chip aceso. */
  fundo: string;
  /** A letra do chip aceso. */
  texto: string;
}

const COR_CHIP: Record<'hoje' | 'amanha' | 'padrao' | 'horas' | 'nenhum', CorChip> = {
  hoje: { forte: '#a16207', fundo: DESTAQUE, texto: '#111111' },
  amanha: { forte: '#1d4ed8', fundo: '#bfdbfe', texto: '#111111' },
  padrao: { forte: '#0e7490', fundo: '#a5f3fc', texto: '#111111' },
  horas: { forte: '#6d28d9', fundo: '#ddd6fe', texto: '#111111' },
  // O silencio e' cinza de proposito: "Nao avisar" e' a unica opcao que nao promete
  // nada, e uma cor viva ali venderia o contrario do que o chip faz.
  nenhum: { forte: '#57534e', fundo: '#d6d3d1', texto: '#111111' },
};

/**
 * A cor dos atalhos de antecedencia.
 *
 * ⚠️ **Os quatro nao ganham quatro cores: eles sao uma ESCALA, nao quatro assuntos.**
 * Ficam na familia do "Horas antes" (que e' de quem eles sao sub-opcao) e clareiam
 * conforme a antecedencia cresce — 1h e' o mais forte, 1 dia o mais claro. Quatro hues
 * diferentes aqui viraria ruido: a fila ja se le pelo numero.
 *
 * ⚠️ Indexada pela POSICAO, nao pelo numero de horas: um atalho novo em
 * `ATALHOS_HORAS` nao pode nascer sem cor. Passou do fim da escala, herda o mais claro.
 */
const ESCALA_ATALHO = ['#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];

function corDoAtalho(posicao: number): CorChip {
  return {
    forte: COR_CHIP.horas.forte,
    fundo: ESCALA_ATALHO[posicao] ?? ESCALA_ATALHO[ESCALA_ATALHO.length - 1]!,
    texto: '#111111',
  };
}

/** O piso da caixa de Observacoes: duas linhas. Dali pra cima ela cresce sozinha. */
const ALTURA_OBS_MIN = 62;

/**
 * O respiro interno dos blocos brancos da folha.
 *
 * ⚠️ Virou constante em 19/08/2026 porque a **regua de Andamento sangra pra fora do
 * bloco com margem negativa deste mesmo valor** — se os dois numeros divergirem, o
 * chip aceso encosta na borda ou sobra um degrau, e nada no typecheck reclama.
 */
const PADDING_BLOCO = 13;

// ---------------------------------------------------------------------------
// Data e hora <-> os campos da tela
// ---------------------------------------------------------------------------

/** `2026-08-19` a partir de um instante, no relogio da loja. */
function campoData(d: Date): string {
  return chaveDoDia(d);
}

/**
 * A hora que o formulario propoe **quando ninguem disse qual** — pelo botao
 * "Novo compromisso". ⚠️ Tocando numa FAIXA da grade quem manda e' a hora tocada
 * (`horaSugerida`), e esta funcao nem e' chamada.
 *
 * ⚠️ Nao e' "agora": compromisso marcado pro minuto em que ele esta digitando nao
 * existe. A proxima hora cheia e' o palpite que quase sempre esta perto e sempre e'
 * futuro — e ele troca em um toque no seletor nativo.
 */
function proximaHoraCheia(agora: Date): string {
  const alvo = new Date(agora.getTime() + 60 * 60 * 1000);
  return `${horaDoDia(alvo).slice(0, 2)}:00`;
}

/** O instante do compromisso a partir dos dois campos, no fuso da loja. */
function instanteDosCampos(data: string, hora: string): Date | null {
  const md = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
  const mh = /^(\d{2}):(\d{2})$/.exec(hora);
  if (!md || !mh) return null;
  return instanteNoFuso(Number(md[1]), Number(md[2]), Number(md[3]), Number(mh[1]), Number(mh[2]));
}

/** O dia vizinho, em `YYYY-MM-DD` no relogio da loja. */
function diaVizinho(dia: string, passos: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  if (!m) return dia;
  // Meio-dia UTC como ancora: somar 24h a partir da meia-noite tropeça em qualquer
  // dia que nao tenha 24 horas, e a conta erraria justamente na virada.
  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  base.setUTCDate(base.getUTCDate() + passos);
  return `${base.getUTCFullYear()}-${String(base.getUTCMonth() + 1).padStart(2, '0')}-${String(base.getUTCDate()).padStart(2, '0')}`;
}

/**
 * O relogio da tela, batendo de meio em meio minuto.
 *
 * ⚠️ **Sem isto a cor mente.** O estado de cada cartao e' calculado do relogio
 * (`estadoDoCompromisso`), e o React so redesenha quando algum estado muda — uma aba
 * deixada aberta ficaria com o compromisso das 13:00 pintado de "futuro" para
 * sempre, inclusive depois das 13:00. E' o mesmo motivo de o servidor NAO mandar a
 * cor pronta: ela e' funcao do tempo, nao um campo.
 *
 * 30 segundos porque o selo mostra minutos; um intervalo de minuto cheio deixaria o
 * "1 min" na tela por ate o dobro do tempo que ele vale.
 */
function useAgora(): Date {
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return agora;
}

// ---------------------------------------------------------------------------
// A LISTA
// ---------------------------------------------------------------------------

export function AgendaApp({ aoContexto }: {
  aoContexto: (c: {
    titulo: string;
    voltar: (() => void) | null;
    aoEditarTitulo?: (() => void) | null;
  }) => void;
}): React.ReactElement {
  const agora = useAgora();
  const [lista, setLista] = useState<CompromissoApp[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  /** O dia que a grade mostra. Abre em HOJE — ele ja esta olhando pra resposta. */
  const [dia, setDia] = useState(() => chaveDoDia(new Date()));
  /** `'novo'` = folha em branco; um objeto = editando aquele. */
  const [editando, setEditando] = useState<CompromissoApp | 'novo' | null>(null);
  /**
   * O dia e a hora que ele tocou na grade — o que a folha nova vai propor.
   *
   * ⚠️ **O DIA vem junto de proposito**: na aba SEMANA a faixa tocada quase nunca e'
   * do `dia` que a tela guarda, e mandar so a hora marcaria no dia errado.
   * `null` = veio pelo botao "Novo compromisso", e a folha propoe o que sempre propos.
   */
  const [sugestao, setSugestao] = useState<{ dia: string; hora: string } | null>(null);
  /**
   * O campo de cliente aparece na folha de um compromisso JA SALVO?
   *
   * ⚠️ **Mora aqui, e nao na `Folha`, porque quem dispara e' a FAIXA do aplicativo** —
   * que e' de fora ([DA-575]) e so alcanca este nivel, pelo `aoContexto`.
   *
   * Fecha a divida da [DA-587]: *"O cliente nao muda"* tirou o campo do compromisso
   * salvo, e nome digitado errado passou a ter como unica saida excluir e remarcar,
   * jogando fora data, aviso, observacao e orcamento por causa de uma letra. ⚠️ **O
   * campo continua fora da folha por padrao** — a decisao dele nao foi revogada, so
   * ganhou uma porta: quem nao vai trocar o nome nao paga os 81px que ele custa.
   */
  const [editandoCliente, setEditandoCliente] = useState(false);
  /** Abre a área de "Meus Clientes" — segundo nível da lista, igual à Folha, mas
   *  nunca os dois ao mesmo tempo (ver o `useEffect` do cabeçalho, abaixo). */
  const [mostrarClientes, setMostrarClientes] = useState(false);
  /** As duas abas pedidas: HOJE (a grade de sempre, intacta) e SEMANA (a mesma
   *  grade, sete dias empilhados). ⛔ Sem "15 DIAS" — ele cortou. */
  const [aba, setAba] = useState<'hoje' | 'semana'>('hoje');
  /** O 1o dia da janela de 7 que a aba SEMANA mostra. Nao e' "a semana ISO" (nao
   *  trava em segunda-feira) — e' uma janela rolante de 7 dias, que e' o que
   *  "sete dias empilhados" pede sem impor um dia fixo de virada. */
  const [semanaInicio, setSemanaInicio] = useState(() => chaveDoDia(new Date()));
  const [semanaLista, setSemanaLista] = useState<CompromissoApp[]>([]);
  const [semanaCarregando, setSemanaCarregando] = useState(true);
  const [semanaErro, setSemanaErro] = useState('');
  /** As categorias (migration 0262) — lista fixa da casa, carregada uma vez aqui
   *  em cima e passada pra Folha; nao muda por prestador nem por compromisso. */
  const [categorias, setCategorias] = useState<CategoriaAgendaApp[]>([]);

  useEffect(() => {
    appApi.agendaCategorias().then(setCategorias).catch(() => setCategorias([]));
  }, []);

  const carregar = useCallback(async (qualDia: string): Promise<void> => {
    setCarregando(true);
    try {
      setLista(await appApi.agendaDoDia(qualDia));
      setErro('');
    } catch (e) {
      setErro((e as ErroApp).message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(dia); }, [carregar, dia]);

  const carregarSemana = useCallback(async (inicio: string): Promise<void> => {
    setSemanaCarregando(true);
    try {
      setSemanaLista(await appApi.agendaDaSemana(inicio, 7));
      setSemanaErro('');
    } catch (e) {
      setSemanaErro((e as ErroApp).message);
    } finally {
      setSemanaCarregando(false);
    }
  }, []);

  // So busca a janela da semana enquanto a aba SEMANA esta aberta — trocar de aba
  // pra HOJE e voltar nao pode disparar uma chamada a mais do que o necessario.
  useEffect(() => {
    if (aba !== 'semana') return;
    void carregarSemana(semanaInicio);
  }, [aba, semanaInicio, carregarSemana]);

  /**
   * Excluir direto da grade, pela lixeira do cartao.
   *
   * ⚠️ **Recarrega o DIA em vez de tirar a linha da lista na mao.** Some por conta
   * propria custaria uma linha a menos, mas mentiria se o servidor tivesse recusado —
   * e agenda que mostra um compromisso a menos do que existe e' pior que agenda
   * nenhuma. O erro, se vier, aparece na mesma faixa dos outros.
   */
  const excluirDaGrade = useCallback(async (id: number): Promise<void> => {
    try {
      await appApi.excluirCompromisso(id);
      setErro('');
      await carregar(dia);
    } catch (e) {
      setErro((e as ErroApp).message);
    }
  }, [carregar, dia]);

  /** O mesmo `excluirDaGrade`, so que recarregando a JANELA da semana em vez do dia. */
  const excluirDaSemana = useCallback(async (id: number): Promise<void> => {
    try {
      await appApi.excluirCompromisso(id);
      setSemanaErro('');
      await carregarSemana(semanaInicio);
    } catch (e) {
      setSemanaErro((e as ErroApp).message);
    }
  }, [carregarSemana, semanaInicio]);

  // O cabecalho do aplicativo e' de fora ([DA-575]); daqui a gente diz o que ele
  // mostra e o que a seta faz. Dentro da folha, a seta volta pra LISTA — se ela
  // despejasse na home, sair de um compromisso fecharia a area inteira.
  useEffect(() => {
    // A área de Clientes cuida do próprio cabeçalho enquanto está aberta (o mesmo
    // padrão da Folha, um nível abaixo) — este efeito ficaria disputando o mesmo
    // `aoContexto` com o dela, entao sai cedo.
    if (mostrarClientes) return;
    if (editando) {
      aoContexto({
        // ⚠️ **Compromisso ja salvo e' nomeado pelo CLIENTE, aqui na faixa** — decisao
        // dele em 19/08 ([DA-587]): *"Retire a palavra cliente e mova o nome do
        // cliente para o topo da tela"*. Como o cliente e' opcional, a palavra
        // generica continua existindo pro compromisso que nasceu sem nome.
        titulo: editando === 'novo'
          ? 'Novo compromisso'
          : (editando.cliente_final ?? '').trim() || 'Compromisso',
        voltar: () => setEditando(null),
        // ⚠️ **So no compromisso SALVO**: na folha em branco o cliente ja e' o
        // primeiro campo, e um lapis no titulo apontaria pra um campo visivel.
        aoEditarTitulo: editando === 'novo' ? null : () => setEditandoCliente(true),
      });
    } else {
      aoContexto({ titulo: 'Agenda', voltar: null, aoEditarTitulo: null });
    }
  }, [editando, mostrarClientes, aoContexto]);

  // Sair da folha fecha o campo — senao o proximo compromisso que ele abrisse
  // nasceria com ele aberto, sem ninguem ter pedido.
  useEffect(() => { if (!editando) setEditandoCliente(false); }, [editando]);

  /**
   * ⚠️ **A sugestao morre com a folha** — senao o proximo toque no botao "Novo
   * compromisso" nasceria com a hora da faixa tocada meia hora antes, sem ninguem
   * ter pedido. Mesmo motivo (e mesmo lugar) do `editandoCliente` acima.
   */
  useEffect(() => { if (!editando) setSugestao(null); }, [editando]);

  /** Tocou numa faixa da grade: folha nova, ja naquele dia e naquela hora cheia. */
  const abrirNaHora = useCallback((qualDia: string, hora: number): void => {
    setSugestao({ dia: qualDia, hora: `${String(hora).padStart(2, '0')}:00` });
    setEditando('novo');
  }, []);

  /** Qual lista recarregar depois de salvar/trocar status — a aba que estava
   *  aberta ANTES de entrar na folha continua sendo `aba` (ela nao muda com a
   *  folha aberta), entao e' o retrato certo de onde o compromisso foi aberto. */
  async function recarregarAbaAtual(): Promise<void> {
    if (aba === 'semana') await carregarSemana(semanaInicio);
    else await carregar(dia);
  }

  function aoSalvar(): void {
    setEditando(null);
    void recarregarAbaAtual();
  }

  /**
   * Trocar o status na folha — e a grade tem que refletir na hora.
   *
   * ⚠️ Recarrega o dia (ou a semana) INTEIRO em vez de remendar a linha na memoria:
   * o compromisso pode ter sido reagendado pra outro dia no mesmo turno, e uma
   * lista remendada mostraria um cartao que nao pertence mais aquele dia.
   */
  async function trocarStatus(id: number, status: StatusCompromisso): Promise<void> {
    await appApi.marcarStatus(id, status);
    await recarregarAbaAtual();
  }

  if (mostrarClientes) {
    return (
      <ClientesApp
        aoContexto={aoContexto}
        aoVoltar={() => setMostrarClientes(false)}
      />
    );
  }

  if (editando) {
    return (
      <Folha
        compromisso={editando === 'novo' ? null : editando}
        diaSugerido={sugestao?.dia ?? dia}
        horaSugerida={sugestao?.hora ?? null}
        categorias={categorias}
        aoSalvar={aoSalvar}
        aoCancelar={() => setEditando(null)}
        aoTrocarStatus={trocarStatus}
        editandoCliente={editandoCliente}
      />
    );
  }

  return (
    <div style={{ padding: '12px 12px 24px' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => setEditando('novo')}
          style={{
            flex: 1, height: 52, borderRadius: 12, backgroundColor: COBRE, color: '#fff',
            fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
          }}
        >
          <Plus size={20} /> Novo compromisso
        </button>
        {/* "Meus Clientes" — segundo nível da Agenda, pedido explícito do escopo:
            os clientes DELE precisam de tela própria pra listar/criar/editar/
            excluir, não só do combo dentro do compromisso. Botão quadrado ao lado
            do primário: é a ferramenta de cadastro, não a ação do dia-a-dia. */}
        <button
          type="button"
          onClick={() => setMostrarClientes(true)}
          aria-label="Meus clientes"
          style={{
            width: 52, height: 52, borderRadius: 12, border: `1px solid ${BORDA}`,
            backgroundColor: '#fff', color: COBRE, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Users size={20} />
        </button>
      </div>

      {/* HOJE / SEMANA — as duas abas do escopo. ⛔ Sem "15 DIAS": ele cortou. */}
      <AbasAgenda aba={aba} aoTrocar={setAba} />

      {aba === 'hoje' ? (
        <>
          {/* ⚠️ **A BarraDia e a Grade abaixo continuam EXATAMENTE como estavam** —
              instrução literal dele: "exibe como hoje, nada muda". O campo de
              busca de data é um IRMÃO ao lado, nunca uma alteração dentro dela. */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BarraDia
                dia={dia}
                agora={agora}
                aoTrocar={setDia}
              />
            </div>
            <CampoBuscaDia aoEscolher={setDia} />
          </div>

          {erro && (
            <div style={{ margin: '10px 2px', padding: 12, borderRadius: 10, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}`, fontSize: 14 }}>
              {erro}
            </div>
          )}

          {carregando ? (
            <div style={{ padding: 24, textAlign: 'center', color: CINZA, fontSize: 14 }}>Carregando…</div>
          ) : (
            <>
              <Grade
                lista={lista}
                dia={dia}
                agora={agora}
                aoAbrir={(c) => setEditando(c)}
                aoExcluir={excluirDaGrade}
                aoTocarHora={abrirNaHora}
              />
              {lista.length === 0 && <Vazio />}
            </>
          )}
        </>
      ) : (
        <Semana
          inicio={semanaInicio}
          lista={semanaLista}
          carregando={semanaCarregando}
          erro={semanaErro}
          agora={agora}
          aoTrocarInicio={setSemanaInicio}
          aoAbrir={(c) => setEditando(c)}
          aoExcluir={excluirDaSemana}
          aoTocarHora={abrirNaHora}
        />
      )}
    </div>
  );
}

/**
 * As duas abas — HOJE (a tela de sempre) e SEMANA (sete dias empilhados).
 *
 * ⚠️ Visual PROPRIO, nao o `.aba-btn` de borda inferior que `OrcamentosApp` usa:
 * a Agenda ja tem sua propria linguagem de cartao branco arredondado (a `BarraDia`
 * logo abaixo), e um par de pilulas no mesmo estilo do destaque `Hoje`/`Amanha` da
 * folha conversa melhor com ela do que uma faixa de borda.
 */
function AbasAgenda({ aba, aoTrocar }: {
  aba: 'hoje' | 'semana';
  aoTrocar: (a: 'hoje' | 'semana') => void;
}): React.ReactElement {
  return (
    <div style={{
      display: 'flex', gap: 4, marginTop: 10, backgroundColor: '#fff',
      border: `1px solid ${BORDA}`, borderRadius: 12, padding: 4,
    }}>
      {(['hoje', 'semana'] as const).map((a) => (
        <button
          key={a}
          type="button"
          onClick={() => aoTrocar(a)}
          aria-pressed={aba === a}
          style={{
            flex: 1, height: 38, borderRadius: 9, fontSize: 13.5,
            fontWeight: aba === a ? 800 : 700,
            backgroundColor: aba === a ? DESTAQUE : 'transparent',
            color: '#1c1917',
          }}
        >
          {a === 'hoje' ? 'Hoje' : 'Semana'}
        </button>
      ))}
    </div>
  );
}

/**
 * O campo de PESQUISA DE DIA — ir direto a uma data, sem navegar seta por seta.
 *
 * ⚠️ **Seletor NATIVO** (`<input type="date">`), mesma razao da Folha: e' o
 * controle mais rapido que existe num celular, no idioma e formato do aparelho
 * dele. Fica RECOLHIDO atras de um botao — a barra de dia/semana ja diz onde ele
 * esta; o campo so aparece pra quem quer PULAR pra outro lugar.
 */
function CampoBuscaDia({ aoEscolher }: { aoEscolher: (dataISO: string) => void }): React.ReactElement {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (aberto) ref.current?.showPicker?.(); }, [aberto]);

  if (aberto) {
    return (
      <input
        ref={ref}
        type="date"
        className="no-upper"
        aria-label="Ir para uma data"
        style={{ ...estiloCampo, width: 46, height: 46, padding: '0 4px', flexShrink: 0 }}
        onChange={(e) => {
          if (e.target.value) { aoEscolher(e.target.value); }
          setAberto(false);
        }}
        onBlur={() => setAberto(false)}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setAberto(true)}
      aria-label="Ir para uma data"
      style={{
        width: 46, height: 46, borderRadius: 12, border: `1px solid ${BORDA}`,
        backgroundColor: '#fff', color: COBRE, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0,
      }}
    >
      <Search size={18} />
    </button>
  );
}

/**
 * A ABA SEMANA — a MESMA grade do dia, sete dias empilhados com rolagem vertical.
 *
 * ⚠️ **Cada dia usa o `<Grade>` de sempre, sem passar nada de novo pra ele** — e'
 * o que garante "faixa cheia por dia (07–19), esticando pelo `faixaDaGrade`" e
 * "NAO recolher as horas vazias": ele nunca soube que existe uma aba SEMANA, entao
 * nao ha como um recolhimento se infiltrar aqui por engano. Medido e aceito por
 * ele (~3.550px, ~6 telas de rolagem) — nao "otimizar" isso.
 *
 * ⛔ Janela ROLANTE de 7 dias a partir de `inicio`, nao a semana ISO (segunda a
 * domingo): "sete dias empilhados" nao pede um dia fixo de virada, e uma janela
 * rolante deixa "Esta semana" sempre comecar em HOJE, que e' o que ele esperaria
 * ao tocar o atalho.
 */
function Semana({ inicio, lista, carregando, erro, agora, aoTrocarInicio, aoAbrir, aoExcluir, aoTocarHora }: {
  /** `YYYY-MM-DD` do primeiro dia da janela de 7. */
  inicio: string;
  /** Os compromissos da janela inteira — a Agenda ja buscou tudo numa chamada so. */
  lista: CompromissoApp[];
  carregando: boolean;
  erro: string;
  agora: Date;
  aoTrocarInicio: (d: string) => void;
  aoAbrir: (c: CompromissoApp) => void;
  aoExcluir: (id: number) => Promise<void>;
  /** ⚠️ Aqui o dia NAO e' o `dia` da tela — e' o da grade tocada, dentro dos sete. */
  aoTocarHora: (dia: string, hora: number) => void;
}): React.ReactElement {
  const hoje = chaveDoDia(agora);
  const dias = useMemo(() => {
    const arr: string[] = [];
    for (let i = 0; i < 7; i += 1) arr.push(diaVizinho(inicio, i));
    return arr;
  }, [inicio]);

  // Agrupa a janela inteira por dia, no fuso da LOJA (`chaveDoDia`) — nunca por
  // `getDate()`, pelo mesmo motivo que a `Grade` do dia agrupa por `horaDoDia`.
  const porDia = useMemo(() => {
    const mapa = new Map<string, CompromissoApp[]>();
    for (const c of lista) {
      const chave = chaveDoDia(new Date(c.quando));
      const atual = mapa.get(chave);
      if (atual) atual.push(c); else mapa.set(chave, [c]);
    }
    return mapa;
  }, [lista]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-start' }}>
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6,
          backgroundColor: '#fff', border: `1px solid ${BORDA}`, borderRadius: 12, height: 46, padding: '0 4px',
        }}>
          <button
            type="button"
            onClick={() => aoTrocarInicio(diaVizinho(inicio, -7))}
            aria-label="Semana anterior"
            style={{ width: 44, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716c' }}
          >
            <ChevronLeft size={22} />
          </button>

          <button
            type="button"
            onClick={() => aoTrocarInicio(hoje)}
            disabled={inicio === hoje}
            style={{ flex: 1, height: 40, textAlign: 'center', backgroundColor: 'transparent', color: '#1c1917', lineHeight: 1.15 }}
          >
            <span style={{ display: 'block', fontSize: 13, fontWeight: 800, textTransform: 'capitalize' }}>
              {rotuloDeDia(new Date(`${dias[0]}T12:00:00-03:00`), agora)} – {rotuloDeDia(new Date(`${dias[6]}T12:00:00-03:00`), agora)}
            </span>
            {inicio !== hoje && (
              <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: COBRE, letterSpacing: 0.3 }}>
                voltar para hoje
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => aoTrocarInicio(diaVizinho(inicio, 7))}
            aria-label="Próxima semana"
            style={{ width: 44, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716c' }}
          >
            <ChevronRight size={22} />
          </button>
        </div>
        <CampoBuscaDia aoEscolher={aoTrocarInicio} />
      </div>

      {erro && (
        <div style={{ margin: '10px 2px', padding: 12, borderRadius: 10, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}`, fontSize: 14 }}>
          {erro}
        </div>
      )}

      {carregando ? (
        <div style={{ padding: 24, textAlign: 'center', color: CINZA, fontSize: 14 }}>Carregando…</div>
      ) : (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {dias.map((d) => (
            <div key={d}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, margin: '10px 2px 6px',
              }}>
                <span style={{
                  fontSize: 13.5, fontWeight: 800, textTransform: 'capitalize',
                  color: d === hoje ? COBRE : '#1c1917',
                }}>
                  {rotuloDeDia(new Date(`${d}T12:00:00-03:00`), agora)}
                </span>
                {d === hoje && (
                  <span style={{
                    fontSize: 10, fontWeight: 900, letterSpacing: 0.5, textTransform: 'uppercase',
                    padding: '2px 6px', borderRadius: 5, backgroundColor: DESTAQUE, color: '#111',
                  }}>
                    hoje
                  </span>
                )}
              </div>
              <Grade
                lista={porDia.get(d) ?? []}
                dia={d}
                agora={agora}
                aoAbrir={aoAbrir}
                aoExcluir={aoExcluir}
                aoTocarHora={aoTocarHora}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A navegacao de dia — as setas que substituiram as abas Proximos/Anteriores.
 *
 * ⚠️ **O botao "Hoje" so aparece quando ele NAO esta em hoje.** Botao que nao faz
 * nada quando tocado ensina a duvidar dos outros ([DA-575] p.9, a mesma razao de a
 * Agenda ter nascido como linha sem `onClick` em vez de botao morto).
 */
function BarraDia({ dia, agora, aoTrocar }: {
  dia: string; agora: Date; aoTrocar: (d: string) => void;
}): React.ReactElement {
  const hoje = chaveDoDia(agora);
  // ⚠️ Meio-dia, nao meia-noite: o rotulo so precisa do DIA, e ancorar no meio da
  // faixa mantem a conta longe de qualquer borda de fuso.
  const rotulo = rotuloDeDia(new Date(`${dia}T12:00:00-03:00`), agora);

  // ⚠️ **A DATA embaixo do rotulo relativo.** `rotuloDeDia` devolve "hoje" / "amanha"
  // / "ontem" nos tres dias vizinhos e a data por extenso nos demais — entao a data
  // so precisa aparecer quando o rotulo NAO a traz. Sem isto, tres dias seguidos da
  // agenda dizem so uma palavra, e ele perde a referencia de onde esta ao navegar.
  const relativo = dia === hoje || dia === diaVizinho(hoje, 1) || dia === diaVizinho(hoje, -1);
  const dataCurta = relativo
    ? new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo', weekday: 'short', day: '2-digit', month: 'short',
    }).format(new Date(`${dia}T12:00:00-03:00`))
    : '';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, marginTop: 12,
      backgroundColor: '#fff', border: `1px solid ${BORDA}`, borderRadius: 12, height: 46, padding: '0 4px',
    }}>
      <button
        type="button"
        onClick={() => aoTrocar(diaVizinho(dia, -1))}
        aria-label="Dia anterior"
        style={{ width: 44, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716c' }}
      >
        <ChevronLeft size={22} />
      </button>

      <button
        type="button"
        onClick={() => aoTrocar(hoje)}
        disabled={dia === hoje}
        style={{
          flex: 1, height: 40, textAlign: 'center', backgroundColor: 'transparent',
          color: '#1c1917', lineHeight: 1.15,
        }}
      >
        <span style={{ display: 'block', fontSize: 15, fontWeight: 800, textTransform: 'capitalize' }}>
          {rotulo}
        </span>
        {dia !== hoje ? (
          <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: COBRE, letterSpacing: 0.3 }}>
            voltar para hoje
          </span>
        ) : dataCurta && (
          <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#a8a29e', letterSpacing: 0.3, textTransform: 'uppercase' }}>
            {dataCurta}
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={() => aoTrocar(diaVizinho(dia, 1))}
        aria-label="Próximo dia"
        style={{ width: 44, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#78716c' }}
      >
        <ChevronRight size={22} />
      </button>
    </div>
  );
}

function Vazio(): React.ReactElement {
  return (
    <div style={{ padding: '14px 20px 0', textAlign: 'center', color: CINZA }}>
      <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>
        Nada marcado para este dia.
      </div>
    </div>
  );
}

/**
 * A GRADE DO DIA — uma faixa por hora, 07h–19h, esticando pra caber o que existe.
 *
 * ⚠️ **As horas VAZIAS sao conteudo, nao moldura.** A lista corrida mostrava o que
 * existe; a grade mostra tambem o que FALTA, que e' a pergunta de quem esta com o
 * cliente na frente perguntando *"da' pra ir amanha de manha?"*. Por isso a faixa
 * vazia continua desenhada, e nao some pra economizar espaco.
 *
 * ⚠️ **O agrupamento por hora e' feito no fuso da LOJA** (`horaDoDia`), nunca com
 * `getHours()`: o compromisso das 22:00 cairia numa faixa errada num navegador com
 * outro fuso, e ninguem repara nisso olhando a propria tela.
 */
function Grade({ lista, dia, agora, aoAbrir, aoExcluir, aoTocarHora }: {
  lista: CompromissoApp[];
  dia: string;
  agora: Date;
  aoAbrir: (c: CompromissoApp) => void;
  aoExcluir: (id: number) => Promise<void>;
  /** Tocou na faixa de uma hora: abre a folha nova JA naquele dia e naquela hora. */
  aoTocarHora: (dia: string, hora: number) => void;
}): React.ReactElement {
  /** Qual cartao esta mostrando a confirmacao no lugar dele. Um por vez. */
  const [confirmando, setConfirmando] = useState<number | null>(null);
  /** Qual esta indo pro servidor — trava os dois botoes daquela confirmacao. */
  const [excluindo, setExcluindo] = useState<number | null>(null);

  const porHora = useMemo(() => {
    const mapa = new Map<number, CompromissoApp[]>();
    for (const c of lista) {
      const h = Number(horaDoDia(new Date(c.quando)).slice(0, 2));
      const atual = mapa.get(h);
      if (atual) atual.push(c); else mapa.set(h, [c]);
    }
    return mapa;
  }, [lista]);

  const [inicio, fim] = faixaDaGrade([...porHora.keys()]);
  const horas: number[] = [];
  for (let h = inicio; h <= fim; h += 1) horas.push(h);

  // O marcador de "agora" so existe no dia de hoje — noutro dia ele seria uma linha
  // afirmando uma hora que nao tem nada a ver com o que esta na tela.
  const ehHoje = dia === chaveDoDia(agora);
  const horaAgora = Number(horaDoDia(agora).slice(0, 2));

  return (
    <div style={{
      marginTop: 12, backgroundColor: '#fff', border: `1px solid ${BORDA}`,
      borderRadius: 14, overflow: 'hidden',
    }}>
      {horas.map((h, i) => (
        <div key={h}>
          {/*
            ⚠️ **A faixa inteira abre o compromisso naquela hora** — pedido dele em
            30/08/2026, com o print apontando a faixa vazia das 13: *"Tem como ao
            tocar no horario o sistema abrir o agendamento?"*. A hora vazia ja era
            conteudo ([DA-644]); agora ela tambem e' a PORTA de marcar ali.

            ⚠️ **A faixa e' `div`, nao `button`** — dentro dela vivem o botao de abrir
            o cartao e o da lixeira, e botao dentro de botao e' HTML invalido (mesma
            razao ja escrita no `Cartao`). O toque em qualquer botao de dentro sobe
            por borbulhamento ate aqui, entao o `closest('button')` devolve o clique
            pra quem era o dono dele — sem isto, tocar na lixeira tambem abriria uma
            folha nova por cima da confirmacao.
          */}
          <div
            role="button"
            tabIndex={-1}
            aria-label={`Marcar às ${String(h).padStart(2, '0')}:00`}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('button')) return;
              aoTocarHora(dia, h);
            }}
            style={{
              display: 'flex', minHeight: 39, cursor: 'pointer',
              borderTop: i === 0 ? 'none' : '1px solid #f2f0ee',
            }}
          >
            <div style={{
              width: 46, flexShrink: 0, fontSize: 12.5, fontWeight: 800,
              color: '#a8a29e', padding: '7px 0 0 10px', letterSpacing: 0.3,
            }}>
              {String(h).padStart(2, '0')}
            </div>
            {/* ⚠️ `minWidth: 0` NAO e' enfeite: sem ele o item flex nao encolhe abaixo
                do texto `nowrap` da observacao, e o cartao vaza pra fora da tela.
                Medido no mockup antes de virar codigo. */}
            <div style={{
              flex: 1, minWidth: 0, padding: '5px 8px 5px 0',
              display: 'flex', flexDirection: 'column', gap: 5,
            }}>
              {(porHora.get(h) ?? []).map((c) => (confirmando === c.id ? (
                <ConfirmarExclusao
                  key={c.id}
                  c={c}
                  ocupado={excluindo === c.id}
                  aoCancelar={() => setConfirmando(null)}
                  aoConfirmar={() => {
                    setExcluindo(c.id);
                    void aoExcluir(c.id).finally(() => {
                      setExcluindo(null);
                      setConfirmando(null);
                    });
                  }}
                />
              ) : (
                <Cartao
                  key={c.id}
                  c={c}
                  agora={agora}
                  aoAbrir={() => aoAbrir(c)}
                  aoPedirExcluir={() => setConfirmando(c.id)}
                />
              )))}
            </div>
          </div>
          {ehHoje && h === horaAgora && <MarcadorAgora agora={agora} />}
        </div>
      ))}
    </div>
  );
}

/**
 * A linha do "agora" atravessando a grade.
 *
 * ⚠️ **E' o que torna a grade legivel sem depender da cor.** Ele nao distingue verde
 * de vermelho ([[user_daltonismo]]); com a linha, "o que ja passou" e "o que ainda
 * vem" ficam separados por POSICAO, que nenhuma paleta consegue estragar.
 */
function MarcadorAgora({ agora }: { agora: Date }): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 0, position: 'relative', zIndex: 2 }}>
      <span style={{
        position: 'absolute', left: 4, fontSize: 10.5, fontWeight: 800,
        color: COBRE, backgroundColor: '#fff', padding: '0 2px',
      }}>
        {horaDoDia(agora)}
      </span>
      <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: COBRE, marginLeft: 42, flexShrink: 0 }} />
      <span style={{ flex: 1, height: 2, backgroundColor: COBRE, opacity: 0.55 }} />
    </div>
  );
}

/**
 * Um compromisso na grade, pintado pelo estado.
 *
 * ⚠️ **A cor e' dele, o selo e' redundancia POR CIMA dela** — nunca no lugar
 * ([[feedback_acrescentar_redundancia_nao_trocar]]). A paleta que ele escolheu tem
 * vermelho, laranja e amarelo em sequencia, que sao os tres mais dificeis de separar
 * pra quem enxerga como ele; trocar as cores seria desfazer a decisao dele, e deixar
 * so a cor seria entregar uma tela que ele nao le.
 */
function Cartao({ c, agora, aoAbrir, aoPedirExcluir }: {
  c: CompromissoApp; agora: Date; aoAbrir: () => void; aoPedirExcluir: () => void;
}): React.ReactElement {
  const quando = new Date(c.quando);
  const estado = estadoDoCompromisso(quando, c.status, agora);
  const pintura = pinturaDoEstado(estado);
  const selo = seloDoCompromisso(quando, c.status, agora);
  const obs = (c.observacoes ?? '').trim().split('\n')[0]?.trim() ?? '';
  const riscado = estado === 'cancelado';

  return (
    /*
      ⚠️ **A moldura e' uma `div`, nao o botao** — e isso e' obrigatorio, nao estilo:
      a lixeira e' um botao, e botao dentro de botao e' HTML invalido (o toque na
      lixeira dispararia os dois). Quem abre o compromisso e' o botao de dentro, que
      ocupa tudo menos a lixeira; a pintura e a borda ficam na moldura.
    */
    <div
      style={{
        width: '100%', display: 'flex', alignItems: 'stretch', borderRadius: 10,
        backgroundColor: pintura.fundo ?? 'transparent',
        color: pintura.texto,
        border: `1px solid ${pintura.borda ?? 'transparent'}`,
      }}
    >
    <button
      type="button"
      onClick={aoAbrir}
      style={{
        flex: 1, minWidth: 0, display: 'flex', gap: 9, alignItems: 'flex-start', textAlign: 'left',
        padding: '8px 2px 8px 10px', backgroundColor: 'transparent', color: 'inherit',
      }}
    >
      <span style={{ fontSize: 15, fontWeight: 800, width: 46, flexShrink: 0 }}>
        {horaDoDia(quando)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontSize: 14.5, fontWeight: 700,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textDecoration: riscado ? 'line-through' : 'none',
        }}>
          {(c.cliente_final ?? '').trim() || 'Sem nome'}
        </span>
        {obs && (
          <span style={{
            display: 'block', fontSize: 12.5, opacity: 0.8, marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textDecoration: riscado ? 'line-through' : 'none',
          }}>
            {obs}
          </span>
        )}
        {c.orcamento_rotulo && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5,
            padding: '2px 7px', borderRadius: 6, border: '1px solid currentColor',
            opacity: 0.75, fontSize: 11.5, maxWidth: '100%',
          }}>
            <FileText size={11} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.orcamento_rotulo}</span>
          </span>
        )}
      </span>

      <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
        {selo && (
          <span style={{
            fontSize: 10, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase',
            padding: '2px 6px', borderRadius: 5, border: '1px solid currentColor', opacity: 0.9,
            whiteSpace: 'nowrap',
          }}>
            {selo}
          </span>
        )}
        {/* O sino diz se aquele compromisso vai chamar ele ou nao. ⚠️ Sem isto, "sem
            aviso" e "com aviso" ficam iguais na grade, e o unico jeito de conferir
            seria abrir um por um. */}
        <span style={{ opacity: c.avisar_em ? 0.85 : 0.35 }}>
          {c.avisar_em ? <Bell size={15} /> : <BellOff size={15} />}
        </span>
      </span>
    </button>

    {/*
      A LIXEIRA no proprio cartao — pedido dele em 20/08/2026: *"tem como incluir uma
      Lixeira aqui, para o usuario, quando quiser, nao precisar entrar no agendamento
      para Excluir?"*. A porta existia so DENTRO da folha (abrir, rolar ate o pe,
      tocar, confirmar): quatro passos e uma tela nova pra desmarcar um compromisso.

      ⚠️ **Ela encosta em quem abre o compromisso, e isso e' aceito de olhos abertos.**
      Num cartao de ~40px a lixeira fica a milimetros do alvo de abrir, e o dedo vai
      errar. Quem paga o erro e' a confirmacao, nao a pontaria — foi a escolha dele
      entre as tres formas, vendo o mockup. ⛔ Nao remover a confirmacao "pra ficar
      mais rapido": ela E' o que torna esta lixeira segura.

      Lixeira, nunca X ([DA-575]): o X do aplicativo quer dizer *fechar*, e dois
      simbolos iguais pra "sair" e "apagar" foi exatamente o que ele mandou desfazer.
    */}
    <button
      type="button"
      onClick={aoPedirExcluir}
      aria-label={`Excluir o compromisso de ${(c.cliente_final ?? '').trim() || 'sem nome'}`}
      style={{
        width: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'transparent', color: 'inherit', opacity: 0.55,
      }}
    >
      <Trash2 size={16} />
    </button>
    </div>
  );
}

/**
 * A confirmacao de excluir, NO LUGAR do cartao.
 *
 * ⚠️ **Ela substitui o proprio cartao dentro da faixa da hora, e nao abre modal.** O
 * compromisso que vai sumir e' o que fica na tela ate a resposta — quem confirma esta
 * olhando pra ele, no horario dele, nao pra um texto por cima de uma grade tapada.
 * Mesmo padrao da lista de orcamentos ([DA-575]).
 *
 * ⚠️ A faixa da hora tem `minHeight: 39` e CRESCE: a confirmacao empurra as horas de
 * baixo enquanto esta aberta, e volta ao normal na resposta. E' o certo — encolher a
 * confirmacao pra caber em 39px daria dois botoes de 20px num celular.
 */
function ConfirmarExclusao({ c, ocupado, aoCancelar, aoConfirmar }: {
  c: CompromissoApp;
  ocupado: boolean;
  aoCancelar: () => void;
  aoConfirmar: () => void;
}): React.ReactElement {
  return (
    <div style={{
      border: '1px solid #f0d5b0', backgroundColor: '#fff8ef',
      borderRadius: 10, padding: '10px 11px',
    }}>
      <div style={{ fontSize: 14, color: '#111' }}>
        Excluir <b>{(c.cliente_final ?? '').trim() || 'sem nome'}</b>, {horaDoDia(new Date(c.quando))}?
      </div>
      <div style={{ fontSize: 12, color: CINZA, marginTop: 2 }}>Não dá pra desfazer.</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
        <button
          type="button"
          onClick={aoCancelar}
          disabled={ocupado}
          style={{
            flex: 1, height: 40, borderRadius: 9, border: '1px solid #d6d3d1',
            backgroundColor: '#fff', color: '#44403c', fontSize: 14, fontWeight: 700,
          }}
        >
          Não
        </button>
        <button
          type="button"
          onClick={aoConfirmar}
          disabled={ocupado}
          style={{
            flex: 1, height: 40, borderRadius: 9, border: 'none',
            backgroundColor: '#b91c1c', color: '#fff', fontSize: 14, fontWeight: 700,
            opacity: ocupado ? 0.6 : 1,
          }}
        >
          {ocupado ? 'Excluindo...' : 'Sim, excluir'}
        </button>
      </div>
    </div>
  );
}

/**
 * O link `wa.me` do lembrete — mesmo padrao do `CatalogoAvisos.tsx` (numero no
 * PATH, `<a target="_blank">`, sem a dança de `window.open`/`noopener` do
 * `BotaoCompartilhar`: aqui nao ha arquivo pra compartilhar, so numero + texto, e
 * `<a target="_blank" rel="noopener noreferrer">` nao sofre o bloqueio de pop-up
 * que `window.open` disparado por JS sofre).
 *
 * ⚠️ **E' um toque do usuario, e so isso** — nao existe envio automatico nesta
 * onda (decisao explicita dele). A mensagem entra PRONTA no campo de texto do
 * WhatsApp; quem aperta enviar e' o prestador, vendo o texto antes.
 */
function linkWhatsappCompromisso(
  telefone: string, nomeCliente: string, quando: Date, observacoes: string, agora: Date,
): string {
  const nome = nomeCliente.trim();
  const assunto = observacoes.trim().split('\n')[0]?.trim() ?? '';
  const msg = `Olá${nome ? ' ' + nome : ''}! Passando para confirmar nosso compromisso `
    + `${rotuloDeDia(quando, agora)} às ${horaDoDia(quando)}${assunto ? ` — ${assunto}` : ''}.`;
  return `https://wa.me/${telefone}?text=${encodeURIComponent(msg)}`;
}

// ---------------------------------------------------------------------------
// A FOLHA — criar e editar
// ---------------------------------------------------------------------------

function Folha({ compromisso, diaSugerido, horaSugerida, categorias, aoSalvar, aoCancelar, aoTrocarStatus, editandoCliente }: {
  compromisso: CompromissoApp | null;
  /** O dia que a grade estava mostrando — e' onde ele quer marcar, nao hoje. */
  diaSugerido: string;
  /**
   * A hora da FAIXA que ele tocou (`HH:00`), ou `null` quando veio pelo botao.
   * ⚠️ So vale pra folha NOVA: num compromisso salvo quem manda e' a hora dele.
   */
  horaSugerida: string | null;
  /** A lista fixa da casa (migration 0262) — carregada uma vez em `AgendaApp`. */
  categorias: CategoriaAgendaApp[];
  aoSalvar: () => void;
  aoCancelar: () => void;
  aoTrocarStatus: (id: number, status: StatusCompromisso) => Promise<void>;
  /** Ele tocou no lapis da faixa: o campo de cliente aparece. Ver a [DA-587]. */
  editandoCliente: boolean;
}): React.ReactElement {
  const agora = useMemo(() => new Date(), []);
  const inicial = compromisso ? new Date(compromisso.quando) : null;
  const topoRef = useRef<HTMLDivElement | null>(null);
  const obsRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * A folha NASCE NO TOPO.
   *
   * ⚠️ O app inteiro rola dentro de um container so ([DA-575]); trocar de tela nao
   * zera o `scrollTop`. Sem isto a folha abre na altura em que a grade estava, e os
   * campos **Data e Hora** — que sao o primeiro bloco — ficam acima da vista.
   * Foi exatamente o que ele relatou em 19/08: *"na hora de criar o compromisso na
   * Agenda nao exibe o campo para selecionar o horario"*. Os campos estavam la; a
   * tela e' que abria rolada.
   */
  useEffect(() => {
    let alvo: HTMLElement | null = topoRef.current?.parentElement ?? null;
    while (alvo && alvo.scrollHeight <= alvo.clientHeight) alvo = alvo.parentElement;
    alvo?.scrollTo({ top: 0 });
  }, []);

  const [data, setData] = useState(inicial ? campoData(inicial) : (diaSugerido || campoData(agora)));
  const [hora, setHora] = useState(
    inicial ? horaDoDia(inicial) : (horaSugerida ?? proximaHoraCheia(agora)),
  );
  const [cliente, setCliente] = useState(compromisso?.cliente_final ?? '');
  /** O ponteiro pro cadastro (migration 0261) — `null` = so o texto livre acima,
   *  o caso comum de antes desta onda. */
  const [clienteId, setClienteId] = useState<number | null>(compromisso?.cliente_id ?? null);
  const [categoriaId, setCategoriaId] = useState<number | null>(compromisso?.categoria_id ?? null);
  /**
   * A lista do cadastro, pro combo — carregada aqui e nao em `AgendaApp` porque so
   * a folha precisa dela (a grade nunca mostra nome de cliente vindo desta lista,
   * so `cliente_final`). `null` enquanto carrega; falha vira lista vazia — o combo
   * ainda funciona pra quem so usa o texto livre ou o botao `+`.
   */
  const [clientesFinais, setClientesFinais] = useState<ClienteFinalApp[] | null>(null);
  const [mostrandoNovoCliente, setMostrandoNovoCliente] = useState(false);
  const [observacoes, setObservacoes] = useState(compromisso?.observacoes ?? '');
  const [aviso, setAviso] = useState<ModoAviso>(compromisso?.aviso ?? 'padrao');
  const [avisoHoras, setAvisoHoras] = useState(
    compromisso?.aviso_horas == null ? '' : String(compromisso.aviso_horas),
  );
  /**
   * Os controles do aviso nascem RECOLHIDOS — a previa (que e' a resposta) fica na
   * cara, e os tres chips so aparecem pra quem vai trocar. Ver o cartao la embaixo.
   */
  const [avisoAberto, setAvisoAberto] = useState(false);
  const [orcamentoId, setOrcamentoId] = useState<number | null>(compromisso?.orcamento_id ?? null);
  const [orcamentos, setOrcamentos] = useState<ResumoOrcamentoApp[] | null>(null);
  const [escolhendo, setEscolhendo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [erro, setErro] = useState('');
  /** Espelho local do status — a folha continua aberta depois de marcar. */
  const [status, setStatus] = useState<StatusCompromisso>(compromisso?.status ?? 'marcado');

  /**
   * Marcar feito / desmarcar, sem sair da folha.
   *
   * ⚠️ **Nao fecha a tela e nao passa pelo botao Salvar.** Fechar descartaria uma
   * edicao de texto que ele tivesse comecado; mandar pelo Salvar faria um toque em
   * "Concluído" reescrever data, hora e aviso a partir do formulario — que e' o
   * motivo de o status ter rota propria no servidor.
   */
  async function trocarStatusAqui(novo: StatusCompromisso): Promise<void> {
    if (!compromisso) return;
    const anterior = status;
    setStatus(novo);
    setSalvando(true);
    setErro('');
    try {
      await aoTrocarStatus(compromisso.id, novo);
    } catch (e) {
      // ⚠️ Volta o espelho: sem isto a folha mostraria "concluído" para um
      // compromisso que o servidor recusou, e ele so descobriria na grade.
      setStatus(anterior);
      setErro((e as ErroApp).message);
    } finally {
      setSalvando(false);
    }
  }

  const quando = instanteDosCampos(data, hora);
  const horasNum = avisoHoras.trim() === '' ? null : Number(avisoHoras.replace(',', '.'));

  // ⚠️ **A frase do aviso sai da MESMA funcao que o servidor usa pra gravar**
  // (`@fiodecobre/shared`). E' o que faz a tela poder prometer o horario: se as duas
  // contas fossem separadas, ela diria "amanhã 06:00" e o cron dispararia outra
  // coisa — e quem nao recebeu o aviso nao tem como saber que devia ter recebido.
  const resultadoAviso = quando ? calcularAvisarEm(quando, aviso, horasNum, new Date()) : null;
  const previa = resultadoAviso ? descreverAviso(resultadoAviso, new Date()) : '';
  /** A previa promete um disparo, ou explica por que nao havera nenhum. */
  const previaAvisa = previa.startsWith('Avisar');

  /**
   * A caixa de Observacoes CRESCE com o texto.
   *
   * ⚠️ Mede o `scrollHeight` depois de zerar a altura — sem o `auto` primeiro, a
   * caixa so cresce e nunca volta a encolher quando ele apaga o texto.
   */
  useEffect(() => {
    const el = obsRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(ALTURA_OBS_MIN, el.scrollHeight)}px`;
  }, [observacoes]);

  // ⚠️ `editandoCliente` (o lapis da faixa, [DA-587]) so decide se este BLOCO
  // aparece — o campo virou combo, e o `<ComboBox>` nao aceita foco programatico
  // vindo de fora (ele abre no toque, como qualquer combo da casa). O ganho da
  // DA-587 (existir um jeito de trocar sem excluir e remarcar) continua de pe; so
  // o "abre ja com o cursor pronto" nao se aplica mais a um campo que se toca pra
  // abrir, nao pra digitar direto.

  /**
   * A lista de clientes do cadastro, pro combo — uma chamada por abertura da
   * folha. `ClientesApp.tsx` e' quem cria/edita/apaga; aqui so se LE, pra montar
   * as opcoes e pro botao `+` devolver o cliente recem-criado com nome pronto.
   */
  useEffect(() => {
    let vivo = true;
    appApi.clientesFinais()
      .then((r) => { if (vivo) setClientesFinais(r); })
      .catch(() => { if (vivo) setClientesFinais([]); });
    return () => { vivo = false; };
  }, []);

  // ⚠️ **Dizer que nao vai avisar e parar ai deixa o caso MAIS COMUM mudo**: marcar
  // algo pra hoje de tarde as 09:00 da manha — o padrao das 06:00 ja passou, e o
  // compromisso nasce sem aviso nenhum. A tela avisa, mas quem esta com pressa nao
  // vai parar pra reconfigurar. Entao ela oferece a saida em UM toque.
  //
  // ⛔ E so oferece quando a saida FUNCIONA: se 1h antes tambem ja passou (o
  // compromisso e' daqui a 20 minutos), o botao nao aparece. Atalho que nao resolve
  // e' pior que atalho nenhum — ele promete e devolve a mesma frase.
  const salvaComUmaHora = quando
    && resultadoAviso?.avisarEm == null
    && resultadoAviso?.motivo !== 'nenhum'
    && calcularAvisarEm(quando, 'horas', 1, new Date()).avisarEm != null;

  async function abrirOrcamentos(): Promise<void> {
    setEscolhendo(true);
    if (orcamentos) return;
    try {
      setOrcamentos(await appApi.orcamentos(false));
    } catch (e) {
      setErro((e as ErroApp).message);
      setEscolhendo(false);
    }
  }

  async function salvar(): Promise<void> {
    if (!quando) { setErro('Confira a data e a hora.'); return; }
    setSalvando(true);
    setErro('');
    const corpo: CorpoCompromisso = {
      data,
      hora,
      cliente_final: cliente.trim() || null,
      cliente_id: clienteId,
      categoria_id: categoriaId,
      orcamento_id: orcamentoId,
      observacoes: observacoes.trim() || null,
      aviso,
      aviso_horas: aviso === 'horas' && horasNum != null && Number.isFinite(horasNum)
        ? Math.round(horasNum) : null,
    };
    try {
      if (compromisso) await appApi.salvarCompromisso(compromisso.id, corpo);
      else await appApi.criarCompromisso(corpo);
      aoSalvar();
    } catch (e) {
      setErro((e as ErroApp).message);
      setSalvando(false);
    }
  }

  async function excluir(): Promise<void> {
    if (!compromisso) return;
    setSalvando(true);
    try {
      await appApi.excluirCompromisso(compromisso.id);
      aoSalvar();
    } catch (e) {
      setErro((e as ErroApp).message);
      setSalvando(false);
    }
  }

  const orcamentoEscolhido = compromisso?.orcamento_id === orcamentoId
    ? compromisso?.orcamento_rotulo ?? null
    : orcamentos?.find((o) => o.id === orcamentoId)?.cliente_final || (orcamentoId ? `Orçamento #${orcamentoId}` : null);

  /**
   * As opções do combo de cliente — a receita de `CatalogoFornecedor.tsx`
   * (`<ComboBox>` + `<ModalCadastroRapido>`), com o detalhe do valor JÁ GRAVADO
   * que não está mais na lista: um compromisso com `cliente_final` de texto livre
   * (o caso comum de antes desta onda, e os 3 compromissos que já existem em
   * produção) entra como opção sintética `(não cadastrada)` — senão o combo abriria
   * vazio num compromisso que TEM cliente, e salvar apagaria o nome que já estava
   * lá ([DA-604]).
   *
   * ⚠️ O valor sintético usa um prefixo de STRING (`TEXTO:`) pra nunca colidir com
   * um id numérico real — é o mesmo truque de espaço de valores que o combo de
   * embalagem usa, só que aqui o "id" observado não é um código, é o texto inteiro.
   */
  const clienteTextoLivre = clienteId == null ? cliente.trim() : '';
  const opcoesCliente = useMemo(() => {
    const base: Array<{ value: string | number; label: string }> =
      (clientesFinais ?? []).map((c) => ({ value: c.id, label: c.nome }));
    if (clienteTextoLivre) {
      base.unshift({ value: `TEXTO:${clienteTextoLivre}`, label: `${clienteTextoLivre} (não cadastrada)` });
    }
    return base;
  }, [clientesFinais, clienteTextoLivre]);

  /**
   * O telefone do cliente ESCOLHIDO — o que liga o botão de WhatsApp abaixo.
   *
   * ⚠️ Prefere o valor AO VIVO da lista carregada (`clientesFinais`) — se ele
   * acabou de editar o telefone em "Meus Clientes" e voltou aqui, o botão usa o
   * numero novo. So cai pro retrato gravado no compromisso (`cliente_telefone`)
   * enquanto a lista ainda não terminou de carregar.
   */
  const clienteEscolhido = clienteId != null ? clientesFinais?.find((c) => c.id === clienteId) : null;
  const telefoneWhats = clienteEscolhido
    ? clienteEscolhido.telefone
    : (compromisso && compromisso.cliente_id === clienteId ? compromisso.cliente_telefone : null);

  return (
    <div
      ref={topoRef}
      style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', padding: '12px 12px 0' }}
    >
      {/*
        O NOME DO CLIENTE VEM PRIMEIRO, e a palavra "Cliente" nao aparece
        ([DA-587], decisao dele em 19/08: *"Retire a palavra cliente e mova o nome do
        cliente para o topo da tela"*).

        ⚠️ **Compromisso JA SALVO nao desenha campo nenhum aqui** — o nome dele e' o
        TITULO, na faixa do aplicativo (`aoContexto`, la em cima). *"O cliente nao
        muda"*: depois de marcado, aquele compromisso e' daquela pessoa, e um campo
        editavel gastava 78px pra oferecer um toque que nao leva a lugar nenhum.

        ✅ **A porta de conserto existe desde 19/08** (a divida que a [DA-587] deixou
        registrada): tocar no lapis da faixa abre este mesmo bloco, agora com o
        rotulo "Cliente" — a palavra volta so aqui, porque sem ela um campo que
        aparece do nada em cima da folha nao diz o que e'.

        ⚠️ **O campo virou COMBO** (escopo desta onda, regra da casa § UI: "campo
        cujo valor vem de um cadastro nasce combo, nunca digitado"). Escolher um
        cliente grava OS DOIS campos juntos — `cliente_id` (o vinculo) e
        `cliente_final` (o texto, pro resto do sistema que ja le esse campo
        continuar funcionando sem mudar). O botao `+` cadastra na hora
        (`ModalNovoClienteFinal`, a mesma receita do `CatalogoFornecedor.tsx`).
      */}
      {(!compromisso || editandoCliente) && (
        <Bloco>
          {compromisso && <Rotulo>Cliente</Rotulo>}
          <div style={{ display: 'flex', gap: 8, marginTop: compromisso ? 6 : 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ComboBox
                value={clienteId ?? (clienteTextoLivre ? `TEXTO:${clienteTextoLivre}` : null)}
                onChange={(v) => {
                  if (v === '' || v == null) { setClienteId(null); setCliente(''); return; }
                  if (typeof v === 'number') {
                    setClienteId(v);
                    setCliente(clientesFinais?.find((c) => c.id === v)?.nome ?? '');
                  }
                  // Valor string que NAO seja number so acontece re-selecionando a
                  // propria opcao sintetica `(nao cadastrada)` — ja e' o estado
                  // atual, entao nao ha nada a fazer.
                }}
                opcoes={opcoesCliente}
                placeholder="Para quem é o serviço"
              />
            </div>
            <button
              type="button"
              onClick={() => setMostrandoNovoCliente(true)}
              aria-label="Cadastrar cliente"
              style={{
                width: 44, height: 36, borderRadius: 8, border: '1px solid var(--input-border)',
                backgroundColor: '#fff', color: COBRE, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-start', marginTop: 2,
              }}
            >
              <Plus size={18} />
            </button>
          </div>
        </Bloco>
      )}

      {/* CATEGORIA — "Visitas" / "Recorrência" (migration 0262). Opcional, sempre;
          "Recorrência" aqui e' SO ROTULO, sem repeticao nenhuma por tras dela. */}
      <Bloco>
        <Rotulo>Categoria</Rotulo>
        <div style={{ marginTop: 6 }}>
          <ComboBox
            value={categoriaId}
            onChange={(v) => setCategoriaId(v === '' || v == null ? null : Number(v))}
            opcoes={categorias.map((c) => ({ value: c.id, label: c.nome }))}
            placeholder="Sem categoria"
          />
        </div>
      </Bloco>

      {/* WHATSAPP — so aparece com compromisso SALVO e cliente com telefone.
          Texto pronto, toque do usuario; nao existe envio automatico. */}
      {compromisso && telefoneWhats && quando && (
        <a
          href={linkWhatsappCompromisso(telefoneWhats, cliente, quando, observacoes, agora)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', height: 46, borderRadius: 11, marginBottom: 12,
            backgroundColor: '#fff', border: `1px solid ${BORDA}`, color: '#15803d',
            fontSize: 14.5, fontWeight: 700,
          }}
        >
          <MessageCircle size={17} /> Avisar por WhatsApp
        </a>
      )}

      <Bloco>
        {/* ⚠️ **Hoje e Amanha moram na LINHA DO ROTULO**, nao numa faixa propria: eles
            custavam 50px de altura sozinhos, e a folha nao cabia na tela do aparelho
            dele nem vazia ([DA-587]). Os rotulos "Data" e "Hora" separados sairam
            junto — um seletor nativo de data e um de hora nao se confundem. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ flex: 1 }}><Rotulo>Data e hora</Rotulo></span>
          <Chip
            mini
            cor={COR_CHIP.hoje}
            ativo={data === campoData(agora)}
            aoTocar={() => setData(campoData(agora))}
          >
            Hoje
          </Chip>
          <Chip
            mini
            cor={COR_CHIP.amanha}
            ativo={data === campoData(new Date(agora.getTime() + 86400000))}
            aoTocar={() => setData(campoData(new Date(agora.getTime() + 86400000)))}
          >
            Amanhã
          </Chip>
        </div>

        {/* Seletor NATIVO de data e hora: num celular ele abre a roda do proprio
            sistema, que e' o controle mais rapido que existe pra isso — e o unico que
            ja vem no idioma e no formato do aparelho dele. */}
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="date"
            className="no-upper"
            style={{ ...estiloCampo, flex: 1.4, width: 'auto' }}
            value={data}
            onChange={(e) => setData(e.target.value)}
            aria-label="Data"
          />
          <input
            type="time"
            className="no-upper"
            style={{ ...estiloCampo, flex: 1, width: 'auto' }}
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            aria-label="Hora"
          />
        </div>
      </Bloco>

      {/*
        OBSERVACOES subiu, e a caixa CRESCE com o texto.

        ⚠️ Ela nao e' nota de rodape: *"vai ser usada quase sempre, principalmente para
        descrever sobre o que e' o agendamento, instalacao, manutencao"* (ele, 19/08).
        E' o assunto do compromisso — e o cartao da grade ja usava a primeira linha
        dela como subtitulo antes de alguem perceber isso.
        ⛔ Altura fixa erra dos dois lados: sobra no compromisso vazio e corta quem
        escreve muito. Duas linhas de piso, e cresce a partir dai.
      */}
      <Bloco>
        <label style={{ display: 'block' }}>
          <Rotulo>Observações</Rotulo>
          <textarea
            ref={obsRef}
            className="no-upper"
            style={{
              ...estiloCampo, marginTop: 6, height: ALTURA_OBS_MIN, minHeight: ALTURA_OBS_MIN,
              padding: '10px 14px', lineHeight: 1.4, resize: 'none', overflow: 'hidden',
            }}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Instalação, manutenção, troca de peça…"
            maxLength={4000}
          />
        </label>
      </Bloco>

      {/*
        AVISO e ORCAMENTO viraram duas FILAS de um cartao so ([DA-587]).

        ⚠️ **A fila do aviso E' a previa** — nao um rotulo que esconde a previa atras
        de um toque. A [DA-578] p.5 fez a tela DIZER o horario do disparo justamente
        pra dissolver todo caso torto sem regra escondida (o compromisso das 05:00 com
        o padrao das 06:00, a antecedencia de 3h num compromisso que e' daqui a 1h), e
        recolher isso teria desfeito a peca que ele desenhou. O que recolhe sao os
        CONTROLES; a resposta fica na cara.
      */}
      <Bloco semRespiro>
        {avisoAberto ? (
          <div style={{ padding: '13px 0' }}>
            <Rotulo>Avisar quando?</Rotulo>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Chip cor={COR_CHIP.padrao} ativo={aviso === 'padrao'} aoTocar={() => setAviso('padrao')}>Manhã do dia</Chip>
              <Chip cor={COR_CHIP.horas} ativo={aviso === 'horas'} aoTocar={() => setAviso('horas')}>Horas antes</Chip>
              <Chip cor={COR_CHIP.nenhum} ativo={aviso === 'nenhum'} aoTocar={() => setAviso('nenhum')}>Não avisar</Chip>
            </div>

            {aviso === 'horas' && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="no-upper"
                    inputMode="numeric"
                    style={{ ...estiloCampo, width: 96, textAlign: 'center' }}
                    value={avisoHoras}
                    onChange={(e) => setAvisoHoras(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
                    placeholder="2"
                    aria-label="Horas de antecedência"
                  />
                  <span style={{ fontSize: 14.5, color: '#44403c' }}>
                    {horasNum === 1 ? 'hora antes' : 'horas antes'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                  {ATALHOS_HORAS.map((h, i) => (
                    <Chip key={h} cor={corDoAtalho(i)} ativo={horasNum === h} aoTocar={() => setAvisoHoras(String(h))}>
                      {h === 24 ? '1 dia' : `${h}h`}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {avisoHoras !== '' && horasNum != null && horasNum > AVISO_HORAS_MAX && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#9a3412' }}>
                O máximo são {AVISO_HORAS_MAX} horas (30 dias).
              </div>
            )}

            <button
              type="button"
              onClick={() => setAvisoAberto(false)}
              style={{
                marginTop: 11, height: 38, width: '100%', borderRadius: 9,
                border: `1px solid ${BORDA}`, backgroundColor: '#fff', color: '#44403c',
                fontSize: 13.5, fontWeight: 700,
              }}
            >
              Pronto
            </button>
          </div>
        ) : (
          /* A FILA do aviso: o icone e o texto sao a previa, e "Trocar" abre os
             controles. Sem `quando` valido nao ha horario pra prometer, entao ela diz
             o que falta em vez de mentir um horario. */
          <button
            type="button"
            onClick={() => setAvisoAberto(true)}
            style={{
              height: 46, width: '100%', display: 'flex', alignItems: 'center', gap: 9,
              background: 'transparent', textAlign: 'left',
            }}
          >
            <span style={{ display: 'flex', color: previaAvisa ? COBRE : '#78716c' }}>
              {previaAvisa ? <Bell size={15} /> : <BellOff size={15} />}
            </span>
            <span style={{
              flex: 1, minWidth: 0, fontSize: 14.5, color: previaAvisa ? '#1c1917' : '#78716c',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {previa || 'Escolha a data e a hora'}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, fontSize: 13, fontWeight: 700, color: COBRE }}>
              Trocar <ChevronRight size={15} />
            </span>
          </button>
        )}

        {/* ⚠️ **A saida de UM TOQUE continua na cara, mesmo com o cartao recolhido**
            ([DA-578] p.5): marcar algo pra hoje de tarde as 09:00 nasce sem aviso
            nenhum, e quem esta com pressa nao para pra reconfigurar. Recolher isto
            atras do "Trocar" transformaria um toque em tres. */}
        {salvaComUmaHora && (
          <button
            type="button"
            onClick={() => { setAviso('horas'); setAvisoHoras('1'); }}
            style={{
              marginBottom: 11, height: 40, width: '100%', borderRadius: 9,
              border: `1px solid ${COBRE}`, backgroundColor: '#fff', color: COBRE,
              fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 7,
            }}
          >
            <Bell size={14} /> Avisar 1 hora antes
          </button>
        )}

        {/* A FILA do orcamento — vazia, ela CONVIDA; cheia, ela mostra qual e' e o X
            tira. O tracejado de 46px com rotulo proprio virou uma linha de texto:
            vincular orcamento e' o passo mais raro desta folha. */}
        <div style={{ height: 46, display: 'flex', alignItems: 'center', gap: 9, borderTop: `1px solid #f2f0ef` }}>
          <span style={{ display: 'flex', color: orcamentoId ? COBRE : '#78716c', flexShrink: 0 }}>
            <FileText size={15} />
          </span>
          {orcamentoId ? (
            <>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, color: '#1c1917', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {orcamentoEscolhido || `Orçamento #${orcamentoId}`}
              </span>
              <button
                type="button"
                onClick={() => setOrcamentoId(null)}
                aria-label="Tirar o orçamento"
                style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CINZA, flexShrink: 0 }}
              >
                <X size={18} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void abrirOrcamentos()}
              style={{ flex: 1, minWidth: 0, height: 46, display: 'flex', alignItems: 'center', gap: 9, background: 'transparent', textAlign: 'left', fontSize: 14.5, color: '#78716c' }}
            >
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Vincular um orçamento
              </span>
              <Plus size={16} style={{ flexShrink: 0, color: COBRE }} />
            </button>
          )}
        </div>
      </Bloco>

      {/* ⛔ So existe em compromisso JA salvo: num formulario em branco, "concluído"
          nao tem sujeito. */}
      {compromisso && (
        <BlocoAndamento
          status={status}
          ocupado={salvando}
          aoTrocar={(s) => void trocarStatusAqui(s)}
        />
      )}

      {erro && (
        <div style={{ margin: '4px 2px 10px', padding: 12, borderRadius: 10, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}`, fontSize: 14 }}>
          {erro}
        </div>
      )}

      <button
        type="button"
        onClick={aoCancelar}
        style={{ width: '100%', height: 46, borderRadius: 11, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 15, fontWeight: 700 }}
      >
        Cancelar
      </button>

      {compromisso && (
        confirmandoExclusao ? (
          // Confirmacao INLINE, no fluxo — o mesmo padrao do "Orcamentos". Modal por
          // cima de um formulario num celular tapa justamente o que ele precisa ler
          // pra decidir.
          <div style={{ marginTop: 16, padding: 13, borderRadius: 11, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}` }}>
            <div style={{ fontSize: 14.5 }}>
              Excluir o compromisso de <b>{(cliente.trim() || 'sem nome')}</b>?
            </div>
            <div style={{ fontSize: 12.5, color: CINZA, marginTop: 3 }}>Não dá pra desfazer.</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
              <button
                type="button"
                onClick={() => setConfirmandoExclusao(false)}
                style={{ flex: 1, height: 44, borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 14.5, fontWeight: 700 }}
              >
                Não
              </button>
              <button
                type="button"
                onClick={() => void excluir()}
                disabled={salvando}
                style={{ flex: 1, height: 44, borderRadius: 9, backgroundColor: DESTAQUE, color: '#000', fontSize: 14.5, fontWeight: 800 }}
              >
                Excluir
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmandoExclusao(true)}
            style={{ width: '100%', height: 46, marginTop: 16, borderRadius: 11, border: `1px solid ${BORDA}`, backgroundColor: '#fff', color: '#9a3412', fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
          >
            <Trash2 size={16} /> Excluir compromisso
          </button>
        )
      )}

      {/*
        O RODAPE FIXO — o Salvar parou de sair da tela.

        🔴 O defeito que abriu esta onda: a folha **nunca coube** no aparelho dele, nem
        em branco. Medido no mockup a 390x739 ([DA-581] p.10 deu a altura util): o
        miolo pedia 821px vazio e 936px preenchido contra 673px de tela, entao salvar
        um compromisso exigia rolar pra achar o botao ([DA-587]).

        ⚠️ **`sticky`, nao `fixed`** — o aplicativo inteiro rola dentro de um container
        so ([DA-575]), e `fixed` se ancora na JANELA: o botao ficaria por cima da grade
        e da home depois de fechar a folha. O `flex: 1` acima empurra a barra pro pe da
        tela quando a folha e' curta, e o `sticky` a segura la quando ela e' longa.
      */}
      <div style={{ flex: 1 }} />
      <div style={{
        position: 'sticky', bottom: 0, marginTop: 12,
        marginLeft: -12, marginRight: -12, padding: '10px 12px 12px',
        backgroundColor: '#f7f7f7', borderTop: `1px solid ${BORDA}`,
      }}>
        <button
          type="button"
          onClick={() => void salvar()}
          disabled={salvando || !quando}
          style={{
            width: '100%', height: 54, borderRadius: 12,
            backgroundColor: salvando || !quando ? '#d6d3d1' : COBRE, color: '#fff',
            fontSize: 16.5, fontWeight: 800, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
          }}
        >
          <Check size={20} /> {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        {/* Botao apagado DIZ por que (regra da casa, § UI) — beco sem saida e' o que
            faz alguem tocar cinco vezes num botao cinza. */}
        {!quando && (
          <div style={{ fontSize: 13, color: CINZA, textAlign: 'center', marginTop: 7 }}>
            Escolha a data e a hora para salvar.
          </div>
        )}
      </div>

      {escolhendo && (
        <EscolherOrcamento
          lista={orcamentos}
          aoEscolher={(id) => { setOrcamentoId(id); setEscolhendo(false); }}
          aoFechar={() => setEscolhendo(false)}
        />
      )}

      {mostrandoNovoCliente && (
        <ModalNovoClienteFinal
          aoFechar={() => setMostrandoNovoCliente(false)}
          aoSalvo={(novo) => {
            setClientesFinais((prev) => [...(prev ?? []), novo].sort((a, b) => a.nome.localeCompare(b.nome)));
            setClienteId(novo.id);
            setCliente(novo.nome);
            setMostrandoNovoCliente(false);
          }}
        />
      )}
    </div>
  );
}

/** O icone de cada chip da regua, na ordem do `ANDAMENTOS`. */
const ICONE_ANDAMENTO: Record<StatusCompromisso, typeof Clock3> = {
  marcado: Clock3,
  vencido: AlertTriangle,
  concluido: CheckCircle2,
  cancelado: Ban,
};

/**
 * ANDAMENTO — a regua do desfecho, em rolagem horizontal.
 *
 * Decisao dele, 19/08/2026: o campo *"O que aconteceu"* virou **Andamento**, e os dois
 * botoes viraram uma fila de quatro — *"Os 4 tocaveis. So Antes do Vencimento que nao
 * aparece."*
 *
 * ⚠️ **Por que o amarelo nao tem chip.** `perto` (falta uma hora) e' o RELOGIO, nao um
 * desfecho: um chip pra ele ofereceria um toque que nao decide nada, e a fila e' de
 * decisoes. Ele continua na grade, onde e' informacao. ⛔ Nao acrescentar depois "pra
 * ficar completo" — completude aqui e' o oposto de legibilidade.
 *
 * ⚠️ **A fila nasce mostrando o chip ACESO, e isso nao e' enfeite.** Os quatro somam
 * ~519px numa faixa de 364px a 390 de largura, entao um deles sempre esta fora. Se ela
 * abrisse sempre no comeco, um compromisso *cancelado* mostraria a fila inteira apagada
 * — a folha pareceria nao ter estado nenhum. Foi o defeito que apareceu no proprio
 * mockup, antes de existir codigo.
 *
 * ⚠️ **A regua SANGRA pras bordas do bloco** (margens negativas): o quarto chip precisa
 * nascer cortado, porque e' o corte que diz que a fila rola. Fila que termina certinha
 * na borda parece uma fila completa.
 *
 * ⚠️ **A volta pra "Agendado" e' um chip como os outros.** Ele vai errar o alvo um dia,
 * e a saida de um toque errado nao pode ser apagar o compromisso e digitar tudo de novo.
 */
function BlocoAndamento({ status, ocupado, aoTrocar }: {
  status: StatusCompromisso;
  ocupado: boolean;
  aoTrocar: (s: StatusCompromisso) => void;
}): React.ReactElement {
  const filaRef = useRef<HTMLDivElement>(null);
  const acesoRef = useRef<HTMLButtonElement>(null);

  // Rola a fila pro chip aceso — ver o cabecalho. `scrollIntoView` moveria a PAGINA
  // junto (o app inteiro rola num container so, [DA-575]); mexer no `scrollLeft` da
  // propria fila nao toca em mais nada.
  useEffect(() => {
    const fila = filaRef.current;
    const aceso = acesoRef.current;
    if (!fila || !aceso) return;
    fila.scrollLeft += aceso.getBoundingClientRect().left
      - fila.getBoundingClientRect().left - PADDING_BLOCO;
  }, [status]);

  return (
    <div style={{ backgroundColor: '#fff', border: `1px solid ${BORDA}`, borderRadius: 14, padding: PADDING_BLOCO, marginBottom: 12 }}>
      <Rotulo>Andamento</Rotulo>

      <div
        ref={filaRef}
        className="sem-barra-rolagem"
        style={{
          display: 'flex', gap: 8, marginTop: 8, overflowX: 'auto',
          // Sangra pras bordas do bloco: o chip cortado e' o que anuncia a rolagem.
          marginLeft: -PADDING_BLOCO, marginRight: -PADDING_BLOCO,
          padding: `2px ${PADDING_BLOCO}px`,
        }}
      >
        {ANDAMENTOS.map(({ status: alvo, rotulo }) => {
          const aceso = status === alvo;
          // ⚠️ A cor sai do MESMO lugar que pinta o cartao da grade
          // (`pinturaDoEstado`), nunca de um literal escrito aqui: duas paletas
          // divergem, e ele veria o chip azul e o cartao de outro azul.
          const pintura = pinturaDoEstado(alvo === 'marcado' ? 'futuro' : alvo);
          const Icone = ICONE_ANDAMENTO[alvo];
          return (
            <button
              key={alvo}
              ref={aceso ? acesoRef : undefined}
              type="button"
              disabled={ocupado}
              aria-pressed={aceso}
              onClick={() => { if (!aceso) aoTrocar(alvo); }}
              style={{
                flexShrink: 0, height: 44, borderRadius: 10, padding: '0 15px',
                display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
                fontSize: 14.5, fontWeight: aceso ? 800 : 700,
                // ⚠️ **TODO chip vem pintado, aceso ou nao** — pedido dele em
                // 20/08/2026, em duas voltas: primeiro *"gostaria que todos os selos ja
                // fossem exibidos na cor atribuida"*, e depois, vendo so as bordas
                // coloridas, *"gostaria que o fundo e a fonte tivessem as cores que
                // estabeleci"*. As cores sao as da grade, escolhidas por ele em 19/08 —
                // fundo E fonte, o par inteiro, nao a cor virada em contorno.
                // ⛔ Nao voltar a apagar os nao-escolhidos: pintar so o aceso deixava a
                // fila INTEIRA cinza no caso mais comum, porque o aceso de um
                // compromisso novo e' *Agendado*, cujo estado na grade e' o sem-cor.
                backgroundColor: pintura.fundo ?? 'transparent',
                color: pintura.texto,
                // O `contorno` (a versao escura da mesma cor) desenha a moldura: sem
                // ela o ambar e o laranja se dissolvem no branco do bloco.
                border: `1px solid ${pintura.contorno}`,
                // ⚠️ **Com os quatro pintados, quem diz o que esta ESCOLHIDO e' o anel
                // preto, nao a cor** — e ele e' acromatico de proposito: a fila tem
                // vermelho, laranja e amarelo em sequencia, os tres que ele menos separa
                // ([[user_daltonismo]]), entao o sinal de selecao nao pode ser mais cor.
                opacity: 1,
                boxShadow: aceso ? '0 0 0 2px #111111' : 'none',
              }}
            >
              <Icone size={16} style={{ flexShrink: 0 }} /> {rotulo}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * `semRespiro` tira o padding de cima e de baixo — pro cartao cujo conteudo sao
 * FILAS de 46px, que ja trazem a propria altura de toque.
 */
function Bloco({ children, semRespiro }: {
  children: React.ReactNode; semRespiro?: boolean;
}): React.ReactElement {
  return (
    <div style={{
      backgroundColor: '#fff', border: `1px solid ${BORDA}`, borderRadius: 14,
      padding: semRespiro ? `0 ${PADDING_BLOCO}px` : PADDING_BLOCO, marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function Rotulo({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>{children}</span>;
}

/**
 * `mini` e' a versao que cabe na LINHA DO ROTULO (Hoje/Amanha ao lado de "Data e
 * hora"), em vez de gastar uma faixa propria de 50px na folha.
 */
function Chip({ ativo, cor, aoTocar, children, mini }: {
  ativo: boolean;
  /** A cor daquele chip — ver o `COR_CHIP`. Aparece aceso E apagado. */
  cor: CorChip;
  aoTocar: () => void;
  children: React.ReactNode;
  mini?: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={aoTocar}
      style={{
        height: mini ? 30 : 40,
        padding: mini ? '0 11px' : '0 14px',
        borderRadius: mini ? 7 : 9,
        fontSize: mini ? 12.5 : 14,
        fontWeight: ativo ? 800 : 700,
        border: `1px solid ${cor.forte}`,
        // Pintado sempre, como a regua ANDAMENTO logo abaixo — a mesma linguagem na
        // mesma folha. Quem diz o que esta escolhido e' o anel preto.
        backgroundColor: cor.fundo,
        color: cor.texto,
        boxShadow: ativo ? '0 0 0 2px #111111' : 'none',
      }}
    >
      {children}
    </button>
  );
}

/**
 * A folha de baixo pra escolher o orcamento vinculado.
 *
 * Folha, e nao dropdown ancorado: a area rola dentro de um `overflow-y: auto`, onde
 * menu absoluto exige `position: fixed` + `getBoundingClientRect` com virada pra cima
 * (regra da casa, § UI). A folha ja e' o padrao do app ([DA-573]) e num celular ela
 * nasce onde o polegar esta.
 */
function EscolherOrcamento({ lista, aoEscolher, aoFechar }: {
  lista: ResumoOrcamentoApp[] | null;
  aoEscolher: (id: number) => void;
  aoFechar: () => void;
}): React.ReactElement {
  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 70, display: 'flex', alignItems: 'flex-end' }}
      onClick={aoFechar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxHeight: '70vh', backgroundColor: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '14px 14px 22px', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ flex: 1, fontSize: 15.5, fontWeight: 800 }}>Vincular orçamento</div>
          <button type="button" onClick={aoFechar} aria-label="Fechar" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CINZA }}>
            <X size={20} />
          </button>
        </div>

        {lista === null ? (
          <div style={{ padding: 20, textAlign: 'center', color: CINZA, fontSize: 14 }}>Carregando…</div>
        ) : lista.length === 0 ? (
          <div style={{ padding: '20px 6px', color: CINZA, fontSize: 14, lineHeight: 1.45 }}>
            Você ainda não tem orçamentos salvos. Monte um em Orçamentos e ele aparece aqui.
          </div>
        ) : (
          lista.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => aoEscolher(o.id)}
              style={{ width: '100%', textAlign: 'left', padding: '12px 4px', borderTop: `1px solid ${BORDA}` }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(o.cliente_final ?? '').trim() || `Orçamento #${o.id}`}
              </div>
              <div style={{ fontSize: 12.5, color: CINZA, marginTop: 2 }}>
                {contar(o.itens, 'item', 'itens')} · R$ {o.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
